import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    ScrollView,
    TouchableOpacity,
    StyleSheet,
    ActivityIndicator
} from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import ListsManager from '../services/ListsManager';
import { useTranslation } from 'react-i18next';

const ChannelItem = ({ channel, isSelected, onToggle }) => {
    return (
        <TouchableOpacity
            style={[styles.channelItem, isSelected && styles.selectedItem]}
            onPress={onToggle}
        >
            <View style={styles.channelInfo}>
                <Text style={styles.channelName}>{channel.name || channel.username}</Text>
                <Text style={styles.channelUsername}>@{channel.username}</Text>
            </View>
            <View style={styles.checkboxContainer}>
                {isSelected ? (
                    <Text style={styles.checkboxSelected}>✓</Text>
                ) : (
                    <View style={styles.checkbox} />
                )}
            </View>
        </TouchableOpacity>
    );
};

const ImportTelegramScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { channels, listID, listName, phoneNumber } = location.state || {};

    const [selectedChannels, setSelectedChannels] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // If channels weren't provided or we have no data, return to the previous screen
    useEffect(() => {
        if (!channels || channels.length === 0) {
            navigate(-1);
        }
    }, [channels, navigate]);

    const toggleChannel = (channel) => {
        setSelectedChannels(prev => {
            if (prev.includes(channel.username)) {
                return prev.filter(username => username !== channel.username);
            } else {
                return [...prev, channel.username];
            }
        });
    };

    const handleImport = async () => {
        if (selectedChannels.length === 0) {
            setError(t('app.importTelegram.pleaseSelect'));
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const success = await ListsManager.selectChannels(
                listID,
                selectedChannels
            );

            setIsLoading(false);

            if (success) {

                // Navigate back to list users screen with refresh flag
                navigate('/list-users/' + listID, {
                    state: {
                        listName,
                        refreshUsers: true,
                        importedChannels: selectedChannels
                    }
                });
            } else {
                setError(t('app.importTelegram.failedImport'));
            }
        } catch (error) {
            console.error('Error importing channels:', error);
            setIsLoading(false);
            setError(t('app.importTelegram.importFailed', { error: error.message || 'Unknown error' }));
        }
    };

    const handleSelectAll = () => {
        if (selectedChannels.length === channels?.length) {
            // If all are selected, deselect all
            setSelectedChannels([]);
        } else if (channels?.length > 0) {
            // Otherwise select all
            setSelectedChannels(channels.map(channel => channel.username));
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    // If channels weren't provided or we have no data, show loading or empty state
    if (!channels || channels.length === 0) {
        return (
            <View style={styles.container}>
                <View style={styles.header}>
                    <TouchableOpacity onPress={handleBack}>
                        <Text style={styles.backButton}>{t('app.importTelegram.back')}</Text>
                    </TouchableOpacity>
                    <Text style={styles.headerTitle}>{t('app.importTelegram.selectChannels')}</Text>
                    <View style={{ width: 50 }} />
                </View>
                <View style={styles.loadingContainer}>
                    <Text style={styles.loadingText}>{t('app.importTelegram.noChannels')}</Text>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity onPress={handleBack}>
                    <Text style={styles.backButton}>{t('app.importTelegram.back')}</Text>
                </TouchableOpacity>
                <Text style={styles.headerTitle}>{t('app.importTelegram.selectChannels')}</Text>
                <View style={{ width: 50 }} />
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#0066cc" />
                    <Text style={styles.loadingText}>{t('app.importTelegram.importingChannels')}</Text>
                </View>
            ) : (
                <>
                    <View style={styles.subHeader}>
                        <Text style={styles.title}>
                            {t('app.importTelegram.title')}
                        </Text>
                        <Text style={styles.subtitle}>
                            {t('app.importTelegram.channelsAvailable', { count: channels.length })}
                        </Text>

                        <TouchableOpacity
                            style={styles.selectAllButton}
                            onPress={handleSelectAll}
                        >
                            <Text style={styles.selectAllText}>
                                {selectedChannels.length === channels.length
                                    ? t('app.importTelegram.deselectAll')
                                    : t('app.importTelegram.selectAll')}
                            </Text>
                        </TouchableOpacity>
                    </View>

                    {error ? <Text style={styles.errorText}>{error}</Text> : null}

                    <ScrollView style={styles.channelsList} contentContainerStyle={styles.channelsListContent}>
                        {channels.map((channel, index) => (
                            <ChannelItem
                                key={channel.username || index}
                                channel={channel}
                                isSelected={selectedChannels.includes(channel.username)}
                                onToggle={() => toggleChannel(channel)}
                            />
                        ))}
                    </ScrollView>

                    <View style={styles.footer}>
                        <TouchableOpacity
                            style={[
                                styles.importButton,
                                selectedChannels.length === 0 && styles.disabledButton
                            ]}
                            onPress={handleImport}
                            disabled={selectedChannels.length === 0}
                        >
                            <Text style={styles.importButtonText}>
                                {selectedChannels.length > 0
                                    ? t('app.importTelegram.importWithCount', { count: selectedChannels.length })
                                    : t('app.importTelegram.import')}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'var(--bg-deep-void)',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
        backgroundColor: 'var(--bg-card)',
        zIndex: 10,
        boxShadow: 'var(--shadow-glass)',
        backdropFilter: 'blur(12px)',
    },
    backButton: {
        color: 'var(--text-primary)',
        fontSize: 14,
        fontWeight: '600',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 12,
        overflow: 'hidden',
        transition: 'all 0.2s',
    },
    headerTitle: {
        color: 'var(--text-primary)',
        fontSize: 18,
        fontWeight: '700',
        letterSpacing: '-0.01em',
        fontFamily: 'Outfit, sans-serif',
    },
    subHeader: {
        padding: 24,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.05)',
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    title: {
        fontSize: 18,
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: 8,
        fontFamily: 'Outfit, sans-serif',
    },
    subtitle: {
        fontSize: 14,
        color: 'var(--text-secondary)',
        marginBottom: 16,
    },
    selectAllButton: {
        alignSelf: 'flex-start',
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.3)',
        transition: 'all 0.2s',
    },
    selectAllText: {
        color: 'var(--accent-primary)',
        fontSize: 14,
        fontWeight: '600',
    },
    channelsList: {
        flex: 1,
    },
    channelsListContent: {
        paddingBottom: 24,
    },
    channelItem: {
        flexDirection: 'row',
        padding: 16,
        paddingHorizontal: 24,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.05)',
        alignItems: 'center',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
    },
    selectedItem: {
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
    },
    channelInfo: {
        flex: 1,
    },
    channelName: {
        fontSize: 16,
        fontWeight: '600',
        color: 'var(--text-primary)',
        marginBottom: 4,
    },
    channelUsername: {
        fontSize: 14,
        color: 'var(--text-secondary)',
    },
    checkboxContainer: {
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkbox: {
        width: 20,
        height: 20,
        borderWidth: 2,
        borderColor: 'var(--text-muted)',
        borderRadius: 6,
        transition: 'all 0.2s',
    },
    checkboxSelected: {
        width: 24,
        height: 24,
        backgroundColor: 'var(--accent-primary)',
        borderRadius: 6,
        textAlign: 'center',
        color: 'white',
        fontWeight: 'bold',
        lineHeight: '24px',
        fontSize: 14,
        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
    },
    footer: {
        padding: 24,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.05)',
        backgroundColor: 'var(--bg-card)',
        zIndex: 10,
    },
    importButton: {
        backgroundColor: 'var(--accent-primary)',
        paddingVertical: 16,
        borderRadius: 16,
        alignItems: 'center',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
        transition: 'all 0.2s',
        cursor: 'pointer',
    },
    disabledButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        boxShadow: 'none',
        cursor: 'not-allowed',
    },
    importButtonText: {
        color: 'white',
        fontWeight: '700',
        fontSize: 16,
        letterSpacing: '0.02em',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    loadingText: {
        color: 'var(--text-secondary)',
        marginTop: 16,
        fontSize: 16,
    },
    errorText: {
        color: 'var(--accent-warning)',
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        padding: 16,
        margin: 16,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(244, 63, 94, 0.2)',
        textAlign: 'center',
    },
});

export default ImportTelegramScreen; 